import { Box } from '@startuptools/ui/Box';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';
import { SideNav } from '../components/side-nav/SideNav';
import { About } from './about/About';
import { Stack } from '@startuptools/ui/Stack';
import { Documents } from './files/Documents';
import { BoardPortal } from './board-portal';
import { ShareRegister } from './share-register/ShareRegister';
import { StockOptions } from './stock-options/StockOptions';
import { Investements } from './investments';
import { Settings } from './settings/Settings';
import { CompanyIdProvider } from '@startuptools/common/react-wrapper';
import { CapTablesRoutes } from './cap-table';
import { DocKind } from '../free-templates/types';
import { GqlCompany } from '../graphql/base-types.graphql';
import { useCompaniesSuspenseQuery } from './Company.graphql';
import { useNavigate } from '../components/Navigate';
import { isEmpty } from 'lodash-es';
import { GenericError } from '@startuptools/ui/Error';
import { FindIdentityProvider } from '@startuptools/business-ui/identity/add/FindIdentityDialog';

const Company = () => {
  const { pathname, companyRootPath } = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();

  if (pathname === `${companyRootPath}/${companyId!}`) {
    return <Navigate to={`${companyRootPath}/${companyId!}/about/dashboard`} />;
  }

  return (
    <CompanyIdProvider companyId={companyId}>
      <FindIdentityProvider>
        <Stack direction="row" sx={{ height: '100dvh' }}>
          <SideNav />
          <Box
            component="main"
            className="MainContent"
            sx={{
              overflow: 'auto',
              px: { xs: 2, md: 3 },
              pt: {
                xs: 'calc(12px + var(--Header-height))',
                sm: 'calc(12px + var(--Header-height))',
                md: 3,
              },
              pb: 8,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              gap: 1,
            }}
          >
            <Outlet />
          </Box>
        </Stack>
      </FindIdentityProvider>
    </CompanyIdProvider>
  );
};

export const CompanyRoutes = ({
  showNewDocumentDialog,
}: {
  showNewDocumentDialog: (
    kind: DocKind,
    company: Pick<GqlCompany, 'id' | 'name' | 'organizationNumber' | 'addressAsStr'>,
  ) => Promise<boolean>;
}) => {
  const { pathname, rootPath, companyRootPath, changeCompanyLink } = useNavigate();
  const { data } = useCompaniesSuspenseQuery({ fetchPolicy: 'cache-and-network', skip: pathname !== companyRootPath });

  if (pathname === companyRootPath) {
    if (data.companies.length === 0) {
      return <Navigate to={`${rootPath}/onboarding`} />;
    }

    let companyId = localStorage.getItem('last_company_id');
    if (isEmpty(companyId) || !data.companies.some(c => c.id === companyId)) {
      companyId = data.companies[0]!.id;
    }
    return <Navigate to={changeCompanyLink(companyId!)} replace />;
  }

  return (
    <Routes>
      <Route path=":companyId" element={<Company />} errorElement={<GenericError />}>
        <Route path="about">
          <Route path="*" element={<About />} />
        </Route>

        <Route path="documents">
          <Route path="*" element={<Documents showNewDocumentDialog={showNewDocumentDialog} />} />
        </Route>

        <Route path="board-portal">
          <Route path="*" element={<BoardPortal />} />
        </Route>

        <Route path="share-register">
          <Route path="*" element={<ShareRegister />} />
        </Route>

        <Route path="cap-tables" element={<CapTablesRoutes />}>
          <Route path="*" element={<CapTablesRoutes />} />
        </Route>

        <Route path="stock-options">
          <Route path="*" element={<StockOptions />} />
        </Route>

        <Route path="investments">
          <Route path="*" element={<Investements />} />
        </Route>

        <Route path="settings">
          <Route path="*" element={<Settings />} />
        </Route>

        <Route path="*" element={<>404 Not found</>} />
      </Route>
    </Routes>
  );
};
